import React from "react";
import { Image, Linking, Modal, Platform, Pressable, StyleSheet, View, useWindowDimensions } from "react-native";
import { XIcon } from "@phosphor-icons/react";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { colors, radii, shadowStyles, spacing, typographyScale } from "../../../styles/design-system";
import { getModalAnimationType } from "../../../utils/getModalAnimationType";

type ExplorationMediaModalType = "video" | "image";

interface ExplorationMediaModalProps {
  visible: boolean;
  type: ExplorationMediaModalType;
  videoId?: string;
  src?: string;
  title?: string;
  description?: string;
  onClose: () => void;
}

const ExplorationMediaModal: React.FC<ExplorationMediaModalProps> = ({
  visible,
  type,
  videoId,
  src,
  title,
  description,
  onClose,
}) => {
  const { height: windowHeight } = useWindowDimensions();
  const isVideo = type === "video";
  const modalLabel = isVideo ? "Close video modal" : "Close image modal";
  const videoUrl = videoId ? `https://www.youtube.com/watch?v=${videoId}` : "";
  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&cc_load_policy=1` : "";
  const imageHeight = Math.max(Math.min(windowHeight * 0.7, 520), 220);
  const imageAlt = title ?? description ?? "Exploration image";

  return (
    <Modal transparent visible={visible} onRequestClose={onClose} animationType={getModalAnimationType("fade")}>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityRole="button" accessibilityLabel={modalLabel}>
        <Pressable
          style={[styles.modalCard, isVideo ? styles.modalCardVideo : styles.modalCardImage]}
          onPress={(e) => e.stopPropagation()}>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={modalLabel}
            style={styles.closeButton}>
            <XIcon size={16} color={colors.white} />
          </Pressable>
          <View
            style={[
              styles.mediaContainer,
              isVideo ? styles.mediaContainerVideo : [styles.mediaContainerImage, { height: imageHeight }],
            ]}>
            {isVideo ? (
              Platform.OS === "web" && embedUrl ? (
                <iframe
                  title={title || "Video"}
                  src={embedUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={styles.videoFrame}
                />
              ) : (
                <View style={styles.videoFallback}>
                  <CustomText
                    text="Video playback is available on web."
                    size="s"
                    style={styles.fallbackText}
                    useTranslationText={false}
                  />
                  {videoUrl ? (
                    <CustomButton
                      text="Open video"
                      styleType="primary"
                      onPress={() => Linking.openURL(videoUrl)}
                      accessibilityLabel="Open video in browser"
                      title="Open video in browser"
                      style={styles.fallbackButton}
                    />
                  ) : null}
                </View>
              )
            ) : src ? (
              <Image
                source={{ uri: src }}
                style={styles.image}
                resizeMode="contain"
                accessibilityLabel={imageAlt}
                alt={imageAlt}
              />
            ) : null}
          </View>
          {(title || description) && (
            <View style={styles.infoContainer}>
              {title ? (
                <CustomText text={title} size="l" weight="bold" style={styles.infoTitle} useTranslationText={false} />
              ) : null}
              {description ? (
                <CustomText
                  text={description}
                  weight="normal"
                  size="s"
                  numberOfLines={3}
                  ellipsizeMode="tail"
                  style={styles.description}
                  useTranslationText={false}
                />
              ) : null}
            </View>
          )}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
    padding: spacing.lg,
  },
  modalCard: {
    width: "100%",
    borderRadius: radii.xl,
    backgroundColor: colors.grey[900],
    overflow: "hidden",
    ...shadowStyles.lg,
  },
  modalCardVideo: {
    maxWidth: 900,
  },
  modalCardImage: {
    maxWidth: 1000,
  },
  closeButton: {
    position: "absolute",
    top: spacing.sm,
    right: spacing.sm,
    padding: spacing.xs,
    zIndex: 1,
  },
  mediaContainer: {
    width: "100%",
    backgroundColor: colors.grey[900],
  },
  mediaContainerVideo: {
    aspectRatio: 16 / 9,
  },
  mediaContainerImage: {
    minHeight: 220,
  },
  videoFrame: {
    borderWidth: 0,
    width: "100%",
    height: "100%",
  },
  videoFallback: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.lg,
    gap: spacing.sm,
  },
  fallbackText: {
    color: colors.grey[200],
  },
  fallbackButton: {
    paddingHorizontal: spacing.lg,
  },
  image: {
    width: "100%",
    height: "100%",
  },
  infoContainer: {
    padding: spacing.md,
    backgroundColor: colors.grey[900],
  },
  infoTitle: {
    color: colors.white,
    marginBottom: spacing.xxs,
    fontSize: typographyScale.textLg.fontSize,
  },
  description: {
    color: colors.grey[400],
  },
});

export default ExplorationMediaModal;
